import { useState, useEffect, useCallback } from "react";
import MiniCupBanner from "../../components/football/MiniCupBanner";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, LogIn, Users, Trophy, Lock, Globe } from "lucide-react";
import { useFb } from "../../utils/football";
import { toast } from "sonner";

export default function FootballLeaguesPage() {
  const fb = useFb();
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [name, setName] = useState("");
  const [visibility, setVisibility] = useState("private");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await fb.get("/football/leagues/my"); setLeagues(d.leagues || []); } catch { /* ignore */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const createLeague = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await fb.post("/football/leagues/create", { name: name.trim(), is_private: visibility === "private" });
      toast.success(`Created! Invite code: ${r.league.code}`);
      setShowCreate(false); setName(""); setVisibility("private");
      navigate(`/football/leagues/${r.league.id}`);
    } catch { toast.error("Could not create league"); }
    setBusy(false);
  };

  const joinLeague = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await fb.post("/football/leagues/join", { code: code.trim().toUpperCase() });
      toast.success("Joined league!");
      setShowJoin(false); setCode("");
      navigate(`/football/leagues/${r.league_id}`);
    } catch { toast.error("League not found"); }
    setBusy(false);
  };

  return (
    <div className="football-theme min-h-screen bg-background pb-12" data-testid="football-leagues-page">
      <div className="px-4 pt-3"><MiniCupBanner /></div>
      <div className="wc-hero-gradient px-4 pb-5 pt-4">
        <button onClick={() => navigate("/")} className="mb-2 inline-flex items-center gap-1 text-sm text-white/80" data-testid="leagues-back"><ChevronLeft size={18} /> Home</button>
        <h1 className="text-xl font-extrabold text-white">My Leagues</h1>
        <p className="mt-1 text-sm text-white/70">Compete with friends in the World Cup.</p>
      </div>

      <div className="mx-auto max-w-md px-4">
        <div className="mt-4 flex gap-2">
          <button onClick={() => { setShowCreate(!showCreate); setShowJoin(false); }} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl wc-bg-gold py-2.5 text-sm font-extrabold text-slate-900" data-testid="leagues-create-btn"><Plus size={16} /> Create</button>
          <button onClick={() => { setShowJoin(!showJoin); setShowCreate(false); }} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-secondary py-2.5 text-sm font-bold" data-testid="leagues-join-btn"><LogIn size={16} /> Join</button>
        </div>

        {showCreate && (
          <div className="mt-3 rounded-xl border border-border bg-card p-3" data-testid="leagues-create-form">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="League name" maxLength={30} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="leagues-create-input" />
            <div className="mt-2 flex gap-2">
              <button onClick={() => setVisibility("private")} className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-bold ${visibility === "private" ? "border-amber-400 wc-bg-gold text-slate-900" : "border-border bg-background text-muted-foreground"}`} data-testid="leagues-visibility-private">
                <Lock size={13} /> Private
              </button>
              <button onClick={() => setVisibility("public")} className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-bold ${visibility === "public" ? "border-amber-400 wc-bg-gold text-slate-900" : "border-border bg-background text-muted-foreground"}`} data-testid="leagues-visibility-public">
                <Globe size={13} /> Public
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">{visibility === "private" ? "Invite-code only — not discoverable." : "Discoverable by anyone in public leagues."}</p>
            <button onClick={createLeague} disabled={busy || !name.trim()} className="mt-2 w-full rounded-lg wc-bg-gold py-2 text-sm font-extrabold text-slate-900 disabled:opacity-50" data-testid="leagues-create-submit">{busy ? "Creating…" : "Create League"}</button>
          </div>
        )}
        {showJoin && (
          <div className="mt-3 rounded-xl border border-border bg-card p-3" data-testid="leagues-join-form">
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="League code (FBL-XXXXXX)" className="w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm" data-testid="leagues-join-input" />
            <button onClick={joinLeague} disabled={busy || !code.trim()} className="mt-2 w-full rounded-lg bg-secondary py-2 text-sm font-bold disabled:opacity-50" data-testid="leagues-join-submit">{busy ? "Joining…" : "Join League"}</button>
          </div>
        )}

        {loading ? <div className="mt-5 h-40 animate-pulse rounded-xl bg-secondary/50" /> : leagues.length === 0 ? (
          <div className="mt-6 rounded-xl border border-border bg-card py-12 text-center" data-testid="leagues-empty">
            <Trophy size={28} className="mx-auto wc-gold" />
            <p className="mt-2 text-sm text-muted-foreground">No leagues yet. Create or join one!</p>
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {leagues.map((l) => (
              <button key={l.id} onClick={() => navigate(`/football/leagues/${l.id}`)} className="flex w-full items-center justify-between rounded-xl border border-border bg-card p-3.5 text-left" data-testid={`league-card-${l.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 truncate font-bold">
                    {l.is_private ? <Lock size={13} className="shrink-0 text-muted-foreground" /> : <Globe size={13} className="shrink-0 wc-gold" />}
                    {l.name}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Users size={12} /> {l.member_count ?? (l.members || []).length} members · <span className="font-mono">{l.code}</span></div>
                </div>
                <ChevronLeft size={18} className="rotate-180 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
